import clsx from "clsx";
import { motion } from "framer-motion";

import { type UniqueIdentifier } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { OPERATOR_ICONS } from "@features/custom-metrics/consts";
import { useFilteredMetrics } from "@features/custom-metrics/hooks/use-filtered-metrics";
import { metrics } from "@north-beam/nb-common";

type Props = {
  isDisabled: boolean;
  token: metrics.Token;
  onRemove: (id: UniqueIdentifier) => void;
  isAnyDragging: boolean;
};

export const FormulaToken = ({
  isDisabled,
  token,
  onRemove,
  isAnyDragging,
}: Props) => {
  const { attributes, setNodeRef, listeners, transform, isDragging } =
    useSortable({
      id: token.id,
      transition: null,
    });

  return (
    <div className="group/operator relative">
      <motion.div
        className={clsx("flex items-center", {
          "rounded bg-interstellar-3 px-3.5":
            token.type === metrics.NODE_TYPE.NUMBER,
          "rounded bg-interstellar-3 px-2.5":
            token.type === metrics.NODE_TYPE.VARIABLE,
          "rounded-full bg-cosmic-3 py-[3px] px-3.5":
            token.type === metrics.NODE_TYPE.OPERATOR,
          "!cursor-grabbing border border-cosmic": isDragging,
          "!cursor-default": isDisabled,
        })}
        style={{
          transformOrigin: "50% 50%",
          transition: "box-shadow 200ms cubic-bezier(0.18, 0.67, 0.6, 1.22)",
          transform: "scale(1.05)",
        }}
        ref={setNodeRef}
        layoutId={String(token.id)}
        animate={
          transform
            ? {
                x: transform.x,
                y: transform.y,
                scale: isDragging ? 1.05 : 1,
                zIndex: isDragging ? 1 : 0,
                boxShadow: isDragging
                  ? "0 0 0 1px rgba(63, 63, 68, 0.05), 0px 8px 8px 0 rgba(34, 33, 81, 0.25)"
                  : undefined,
              }
            : { x: 0, y: 0, scale: 1 }
        }
        transition={{
          duration: isDragging ? 0 : 0.25,
          easings: { type: "spring" },
          scale: { duration: 0.25 },
          zIndex: { delay: isDragging ? 0 : 0.25 },
        }}
        {...attributes}
        {...listeners}
      >
        <div className="text-sm">
          <TokenValue token={token} />
        </div>
      </motion.div>
      {!isDisabled && (
        <button
          className={clsx(
            {
              "group-hover/operator:opacity-100 hover:bg-mars":
                !isDragging && !isAnyDragging,
            },
            "opacity-0 rounded-full bg-mars-1 flex-center text-xs text-white",
            "absolute -top-1 -right-1 h-3.5 w-3.5",
            "transition-opacity duration-300",
          )}
          onClick={() => onRemove(token.id)}
        >
          <i className="fa-regular fa-xmark" />
        </button>
      )}
    </div>
  );
};

const TokenValue = ({ token }: { token: metrics.Token }) => {
  const { getMetricById } = useFilteredMetrics();

  if (token.type === metrics.NODE_TYPE.NUMBER) {
    return <>{token.value}</>;
  } else if (token.type === metrics.NODE_TYPE.OPERATOR) {
    return (
      <i
        className={clsx(
          "fa-regular text-sm",
          `fa-${OPERATOR_ICONS[token.value as metrics.Operator]}`,
        )}
      />
    );
  }

  const metric = getMetricById(token.value);
  if (metric) {
    return <>{metric.name}</>;
  }

  return <>Unknown metric</>;
};
